import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { clientsService } from "@/api/services";
import type { Client } from "@/types/domain";
import { StatusBadge } from "@/components/common/StatusBadge";
import { useI18n } from "@/i18n/I18nProvider";

export default function ClientsPage() {
  const { t } = useI18n();

  const [clients, setClients] = useState<Client[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [query, setQuery] = useState("");
  const [statutFiltre, setStatutFiltre] =
    useState<"ALL" | "actif" | "inactif">("ALL");

  const [showCreateForm, setShowCreateForm] = useState(false);

  const [newCodeClient, setNewCodeClient] = useState("");
  const [newNomLegal, setNewNomLegal] = useState("");
  const [newNomCommercial, setNewNomCommercial] = useState("");
  const [newTypeClient, setNewTypeClient] = useState("client");
  const [newStatut, setNewStatut] = useState("actif");
  const [newCourrielGeneral, setNewCourrielGeneral] = useState("");
  const [newTelephoneGeneral, setNewTelephoneGeneral] = useState("");
  const [newLangue, setNewLangue] = useState("fr-CA");
  const [newDevise, setNewDevise] = useState("CAD");

  const [createSubmitting, setCreateSubmitting] = useState(false);
  const [createError, setCreateError] = useState<string | null>(null);

  useEffect(() => {
    const loadClients = async () => {
      try {
        setLoading(true);
        setError(null);

        const data = await clientsService.list();
        setClients(data);
      } catch (err) {
        console.error("Erreur chargement clients :", err);
        setError("Impossible de charger les clients.");
      } finally {
        setLoading(false);
      }
    };

    loadClients();
  }, []);

  const handleCreateClient = async (
    e: React.FormEvent<HTMLFormElement>
  ) => {
    e.preventDefault();

    if (!newCodeClient.trim() || !newNomLegal.trim()) {
      setCreateError(
        "Le code client et le nom légal sont obligatoires."
      );
      return;
    }

    try {
      setCreateSubmitting(true);
      setCreateError(null);

      await clientsService.create({
        code_client: newCodeClient.trim(),
        nom_legal: newNomLegal.trim(),
        nom_commercial: newNomCommercial.trim(),
        type_client: newTypeClient,
        statut: newStatut,
        courriel_general: newCourrielGeneral.trim(),
        telephone_general: newTelephoneGeneral.trim(),
        langue: newLangue,
        devise: newDevise,
      });

      const data = await clientsService.list();
      setClients(data);

      setNewCodeClient("");
      setNewNomLegal("");
      setNewNomCommercial("");
      setNewTypeClient("client");
      setNewStatut("actif");
      setNewCourrielGeneral("");
      setNewTelephoneGeneral("");
      setNewLangue("fr-CA");
      setNewDevise("CAD");

      setShowCreateForm(false);
    } catch (err) {
      console.error("Erreur création client :", err);
      setCreateError("Impossible de créer le client.");
    } finally {
      setCreateSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="p-6">
        Chargement des clients...
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-6">
        {error}
      </div>
    );
  }

  const clientsFiltres = clients.filter((client) => {
    const q = query.trim().toLowerCase();

    const matchStatut =
      statutFiltre === "ALL" ||
      client.statut === statutFiltre;

    const matchRecherche =
      !q ||
      client.code_client.toLowerCase().includes(q) ||
      client.nom_legal.toLowerCase().includes(q) ||
      (client.nom_commercial?.toLowerCase().includes(q) ?? false) ||
      (client.courriel_general?.toLowerCase().includes(q) ?? false) ||
      (client.telephone_general?.toLowerCase().includes(q) ?? false);

    return matchStatut && matchRecherche;
  });

  return (
    <div className="space-y-6 p-6">
      <div>
        <h1 className="text-2xl font-semibold">
          {t.modules.clients.title}
        </h1>

        <p className="text-sm text-muted-foreground">
          {t.modules.clients.subtitle}
        </p>
      </div>

      <div className="flex justify-end">
        <button
          type="button"
          onClick={() => {
            setCreateError(null);
            setShowCreateForm((current) => !current);
          }}
          className="rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground hover:opacity-90"
        >
          {showCreateForm ? "Annuler" : "+ Nouveau client"}
        </button>
      </div>

      {showCreateForm && (
        <form
          onSubmit={handleCreateClient}
          className="space-y-4 rounded-lg border border-border bg-card p-5"
        >
          <div>
            <h2 className="text-lg font-semibold">
              Créer un client
            </h2>

            <p className="text-sm text-muted-foreground">
              Ajouter un nouveau client dans Rockfall Operations Center.
            </p>
          </div>

          {createError && (
            <div className="rounded-md border border-destructive/30 bg-destructive/10 p-3 text-sm text-destructive">
              {createError}
            </div>
          )}

          <div className="grid gap-4 md:grid-cols-2">
            <label className="space-y-1">
              <span className="text-sm font-medium">
                Code client *
              </span>

              <input
                value={newCodeClient}
                onChange={(e) => setNewCodeClient(e.target.value)}
                placeholder="Ex. CLI-006"
                required
                className="w-full rounded-md border border-border bg-background px-3 py-2 text-sm"
              />
            </label>

            <label className="space-y-1">
              <span className="text-sm font-medium">
                Nom légal *
              </span>

              <input
                value={newNomLegal}
                onChange={(e) => setNewNomLegal(e.target.value)}
                required
                className="w-full rounded-md border border-border bg-background px-3 py-2 text-sm"
              />
            </label>

            <label className="space-y-1">
              <span className="text-sm font-medium">
                Nom commercial
              </span>

              <input
                value={newNomCommercial}
                onChange={(e) =>
                  setNewNomCommercial(e.target.value)
                }
                className="w-full rounded-md border border-border bg-background px-3 py-2 text-sm"
              />
            </label>

            <label className="space-y-1">
              <span className="text-sm font-medium">
                Type
              </span>

              <select
                value={newTypeClient}
                onChange={(e) => setNewTypeClient(e.target.value)}
                className="w-full rounded-md border border-border bg-background px-3 py-2 text-sm"
              >
                <option value="client">Client</option>
                <option value="prospect">Prospect</option>
              </select>
            </label>

            <label className="space-y-1">
              <span className="text-sm font-medium">
                Statut
              </span>

              <select
                value={newStatut}
                onChange={(e) => setNewStatut(e.target.value)}
                className="w-full rounded-md border border-border bg-background px-3 py-2 text-sm"
              >
                <option value="actif">Actif</option>
                <option value="inactif">Inactif</option>
              </select>
            </label>

            <label className="space-y-1">
              <span className="text-sm font-medium">
                Courriel général
              </span>

              <input
                type="email"
                value={newCourrielGeneral}
                onChange={(e) =>
                  setNewCourrielGeneral(e.target.value)
                }
                className="w-full rounded-md border border-border bg-background px-3 py-2 text-sm"
              />
            </label>

            <label className="space-y-1">
              <span className="text-sm font-medium">
                Téléphone général
              </span>

              <input
                value={newTelephoneGeneral}
                onChange={(e) =>
                  setNewTelephoneGeneral(e.target.value)
                }
                className="w-full rounded-md border border-border bg-background px-3 py-2 text-sm"
              />
            </label>

            <label className="space-y-1">
              <span className="text-sm font-medium">
                Langue
              </span>

              <select
                value={newLangue}
                onChange={(e) => setNewLangue(e.target.value)}
                className="w-full rounded-md border border-border bg-background px-3 py-2 text-sm"
              >
                <option value="fr-CA">
                  Français (Canada)
                </option>
                <option value="en-CA">
                  English (Canada)
                </option>
              </select>
            </label>

            <label className="space-y-1">
              <span className="text-sm font-medium">
                Devise
              </span>

              <select
                value={newDevise}
                onChange={(e) => setNewDevise(e.target.value)}
                className="w-full rounded-md border border-border bg-background px-3 py-2 text-sm"
              >
                <option value="CAD">CAD</option>
                <option value="USD">USD</option>
              </select>
            </label>
          </div>

          <div className="flex justify-end gap-2">
            <button
              type="button"
              onClick={() => {
                setCreateError(null);
                setShowCreateForm(false);
              }}
              className="rounded-md border border-border px-4 py-2 text-sm font-medium"
            >
              Annuler
            </button>

            <button
              type="submit"
              disabled={createSubmitting}
              className="rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground disabled:cursor-not-allowed disabled:opacity-50"
            >
              {createSubmitting
                ? "Création..."
                : "Créer le client"}
            </button>
          </div>
        </form>
      )}

      <div className="flex flex-wrap gap-3">
        <input
          type="search"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder="Rechercher un client..."
          className="w-full max-w-md rounded-md border border-border bg-background px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-ring"
        />

        <select
          value={statutFiltre}
          onChange={(e) =>
            setStatutFiltre(
              e.target.value as "ALL" | "actif" | "inactif"
            )
          }
          className="rounded-md border border-border bg-background px-3 py-2 text-sm"
        >
          <option value="ALL">Tous</option>
          <option value="actif">Actifs</option>
          <option value="inactif">Inactifs</option>
        </select>
      </div>

      <div className="overflow-hidden rounded-lg border border-border">
        <table className="w-full text-sm">
          <thead className="bg-muted/50">
            <tr className="border-b border-border">
              <th className="px-4 py-3 text-left">Code</th>
              <th className="px-4 py-3 text-left">Client</th>
              <th className="px-4 py-3 text-left">Type</th>
              <th className="px-4 py-3 text-left">Statut</th>
              <th className="px-4 py-3 text-left">Courriel</th>
              <th className="px-4 py-3 text-left">Téléphone</th>
            </tr>
          </thead>

          <tbody>
            {clientsFiltres.map((client) => (
              <tr
                key={client.id}
                className="border-b border-border last:border-b-0"
              >
                <td className="px-4 py-3 font-mono">
                  {client.code_client}
                </td>

                <td className="px-4 py-3">
                  <Link
                    to={`/clients/${client.id}`}
                    className="font-medium hover:underline"
                  >
                    {client.nom_commercial || client.nom_legal}
                  </Link>

                  {client.nom_commercial && (
                    <div className="text-xs text-muted-foreground">
                      {client.nom_legal}
                    </div>
                  )}
                </td>

                <td className="px-4 py-3">
                  {client.type_client}
                </td>

                <td className="px-4 py-3">
                  <StatusBadge status={client.statut} />
                </td>

                <td className="px-4 py-3">
                  {client.courriel_general || "—"}
                </td>

                <td className="px-4 py-3">
                  {client.telephone_general || "—"}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}